import type { ReactNode } from 'react';
import { strings } from '@/data/strings';
import styles from './Footer.module.css';

// landing=true: anchors en-página (#x). landing=false: links absolutos al landing (/#x).
export function Footer({ landing = false }: { landing?: boolean } = {}) {
  const hrefBase = landing ? '' : '/';
  const brandHref = landing ? '#top' : '/';
  // Solo prefija los hrefs de anchor (#...). Los links a rutas (/terms, /soporte) pasan derecho.
  const anchorHref = (href: string) => (href.startsWith('#') ? `${hrefBase}${href}` : href);

  return (
    <footer className={styles.footer}>
      <div className={`container ${styles.inner}`}>
        <div className={styles.grid}>
          <div className={styles.brandCol}>
            <a
              href={brandHref}
              className={styles.brand}
              aria-label={strings.footer.brandAriaHome}
            >
              <span className={styles.brandText}>{strings.brand.name}</span>
              <span className={styles.brandDot} aria-hidden="true" />
            </a>
            <p className={styles.pitch}>{strings.footer.pitch}</p>
            <ul
              className={styles.social}
              aria-label={strings.footer.social.groupLabel}
            >
              {strings.footer.social.items.map((s) => (
                <li key={s.key}>
                  <a
                    href={s.href}
                    className={styles.socialLink}
                    aria-label={s.label}
                    rel="noopener noreferrer"
                  >
                    <SocialIcon name={s.key} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.col}>
            <h3 className={styles.colTitle}>
              {strings.footer.columnTitles.product}
            </h3>
            <ul className={styles.list}>
              {strings.footer.product.map((link) => (
                <li key={link.href}>
                  <a href={anchorHref(link.href)} className={styles.link}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.col}>
            <h3 className={styles.colTitle}>
              {strings.footer.columnTitles.legal}
            </h3>
            <ul className={styles.list}>
              {strings.footer.legal.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className={styles.link}>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>{strings.footer.copyright}</p>
          <p className={styles.madeIn}>
            <span>{strings.footer.madeIn}</span>
            <FlagAR />
          </p>
        </div>
      </div>
    </footer>
  );
}

function SocialIcon({ name }: { name: 'instagram' | 'linkedin' }): ReactNode {
  if (name === 'instagram') {
    return (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.55V9h3.57v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.55C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.72C24 .77 23.2 0 22.22 0Z" />
    </svg>
  );
}

function FlagAR() {
  return (
    <svg
      className={styles.flag}
      width="18"
      height="12"
      viewBox="0 0 18 12"
      aria-hidden="true"
    >
      <rect width="18" height="12" rx="2" fill="#FFFFFF" />
      <rect width="18" height="4" fill="#74ACDF" />
      <rect y="8" width="18" height="4" fill="#74ACDF" />
      <circle cx="9" cy="6" r="1.6" fill="#F6B40E" />
    </svg>
  );
}
